import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";

import PaperFragment from "../../../../helper-components/misc/PaperFragment";
import StackedHeader from "../../../../helper-components/misc/StackedHeader";
import DataGridTable from "../../../../helper-components/table/DataGridTable";

import PatientEditForm from "./PatientEditForm";
import PatientAddStepper from "./PatientAddStepper";
import SimilarPatientsDialog from "./SimilarPatientsDialog";

import { createUploadObject, setInsertArrayState } from "../../../../helper-functions/shared/miscUtils";
import { getPatientDisplayName, normalizePatientRowForUpload } from "../../../../helper-functions/shared/patientDisplay";
import { columns, defaultTableOptions } from "../../../../helper-functions/table/patients";
import {
    createPatient,
    deletePatients,
    getDuplicatePatientBlockedMessage,
    getExistingPatientReuseMessage,
    matchPatients,
    queryPatients,
    updatePatient
} from "../../../../helper-functions/api/patientQueries";

import { getLanguage } from "../../../../config/language/language";
import { PATIENT_NULLABLE_FIELDS } from "../../../../config/constants/Constants";

/**
 * Table showing patients.
 * If no data found, show error message instead.
 */
export default function PatientsTable(props) {
    const patients = props.backend.patients;

    const showCreateDialog = () => {
        props.showDialog({
            title: "Add new patient",
            component: <PatientAddStepper {...props} callback={(confirm, options) => handleStepper(confirm, options)} />
        });
    };

    const showDeleteDialog = () => {
        props.showDialog({
            title: "Delete patient(s)",
            message: `Are you sure you want to delete the ${patients.selected.length} selected patient(s)?

            All data, including transcripts and quotes, will be lost.`,
            actionTitle: "Delete",
            actionColor: "error",
            callback: (confirm) => handleDelete(confirm)
        });
    };

    const showSimilarDialog = () => {
        props.showDialog({
            title: "Similar patient names",
            component: <SimilarPatientsDialog {...props} />
        });
    };

    const showUpdateDialog = () => {
        props.showDialog({
            title: "Edit patient",
            message: "Click 'Update' to confirm changes.",
            component: <PatientEditForm {...props} row={patients.selected?.[0]} callback={(confirm, options) => handleUpdate(confirm, options)} />
        });
    };

    const handleStepper = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        handleCreate({ row: normalizePatientRowForUpload(row) });
    };

    const handleCreate = async (options = {}) => {
        const { row } = options;
        const matches = await matchPatients(props, row);

        if (matches.length === 1) {
            props.showDialog({
                title: "Existing patient found",
                message: getExistingPatientReuseMessage(matches[0]),
                actionTitle: "Use existing patient",
                callback: (confirm) => {
                    if (confirm) {
                        setInsertArrayState(props.backend.patients.setData, [matches[0]]);
                        props.showSnackbar({ color: "info", message: `Selected existing patient: ${getPatientDisplayName(matches[0])}` });
                        return;
                    }

                    props.showSnackbar({
                        color: "error",
                        message: getDuplicatePatientBlockedMessage(row)
                    });
                }
            });
            return;
        }

        const body = createUploadObject(row, ["form", "_*"], ["*"], PATIENT_NULLABLE_FIELDS);
        props.showSnackbar({ color: "info", message: getLanguage().SNACKBAR_INFO_LOADING, duration: 10000 });
        createPatient(props, { body });
    };

    const handleDelete = (confirm) => {
        if (!confirm) return;

        const ids = patients.selected.map((patient) => patient.id);
        deletePatients(props, { body: { ids }, ids });
    };

    const handleUpdate = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const cleanRow = normalizePatientRowForUpload(row);
        const body = createUploadObject(cleanRow, ["form", "_*", "id", "created_on", "updated_on", "examinations"], ["*"], PATIENT_NULLABLE_FIELDS);
        updatePatient(props, { body, id: row.id });
    };

    return (
        <PaperFragment
            className="tour-patients-3 tour-patients-4 tour-patients-5 tour-patients-6"
            title={
                <StackedHeader
                    {...props}
                    title="Patients"
                    subtitle="View and manage patients."
                    icons={[
                        {
                            icon: <DeleteIcon />,
                            tooltip: "Delete selected patients",
                            disabled: patients.selected.length === 0,
                            onClick: showDeleteDialog
                        },
                        {
                            icon: <EditIcon />,
                            tooltip: "Edit selected patient",
                            onClick: showUpdateDialog,
                            disabled: patients.selected.length !== 1
                        },
                        {
                            icon: <VisibilityIcon />,
                            tooltip: "View selected patient",
                            onClick: () => {
                                const id = patients.selected?.[0]?.id;
                                if (id) props.browser.navigate(`/database/patients/${id}`);
                            },
                            disabled: patients.selected.length !== 1
                        },
                        {
                            icon: <PersonSearchIcon />,
                            className: "tour-patients-similar",
                            tooltip: "Find similar patient names",
                            onClick: showSimilarDialog
                        }
                    ]}
                    buttons={[
                        {
                            icon: <AddIcon />,
                            title: "New patient",
                            onClick: showCreateDialog
                        }
                    ]}
                />
            }
            value={
                <DataGridTable
                    {...props}
                    {...defaultTableOptions}
                    rowHeight={60}
                    states={patients}
                    columns={columns(props)}
                    query={(options) => queryPatients(props, options)}
                    empty={getLanguage().NO_PATIENTS_FOUND}
                    refresh={10}
                />
            }
        />
    );
}
